<template>
  <app-card :status="competition.status" class="flex flex-col">
    <div class="flex flex-col p-4">
      <header>
        <h3 class="leading-tight mb-2">
          {{ competition.title }}
        </h3>
        <p
          v-if="competition.funding_pots[0]"
          class="text-xs text-grey-600 leading-none"
        >
          <strong v-if="competition.private" class="text-primary-500">
            <icon
              icon="security-check"
              class="w-4 h-4 icons-primary align-middle"
            />
            Private:
          </strong>
          {{ competition.funding_pots[0].name }}
        </p>
        <div class="mt-4 mb-4 text-sm border-t pt-4 h-full">
          <p
            v-if="userHasProject || canApply"
            class="text-xs text-grey-600 mb-4"
            v-html="$sanitizeHtml(applicationDate)"
          />
          <div
            v-if="competition.overview"
            v-html="
              parseMarkdown(
                $sanitizeHtml(competition.overview.substring(0, 240)) + '...'
              )
            "
          />
        </div>
      </header>

      <template
        v-if="
          competition.min_grant_per_project || competition.max_grant_per_project
        "
      >
        <ul class="list-reset flex justify-between">
          <li class="w-1/2 py-2">
            <h4 class="text-2xs tracking-wide uppercase text-grey-600">
              Min Project Grant
            </h4>
            <p class="text-2xl text-primary-700 leading-none">
              <template v-if="competition.min_grant_per_project">
                £{{ competition.min_grant_per_project.toLocaleString("en-EN") }}
              </template>
              <template v-else>
                --
              </template>
            </p>
          </li>
          <li class="w-1/2 py-2">
            <h4 class="text-2xs tracking-wide uppercase text-grey-600">
              Max Project Grant
            </h4>
            <p class="text-2xl text-primary-700 leading-none">
              <template v-if="competition.max_grant_per_project">
                £{{ competition.max_grant_per_project.toLocaleString("en-EN") }}
              </template>
              <template v-else>
                --
              </template>
            </p>
          </li>
        </ul>
      </template>

      <template
        v-if="competition.min_project_costs || competition.max_project_costs"
      >
        <ul class="list-reset flex justify-between">
          <li class="w-1/2 py-2">
            <h4 class="text-2xs tracking-wide uppercase text-grey-600">
              Min Project Costs
            </h4>
            <p class="text-2xl text-primary-700 leading-none">
              <template v-if="competition.min_project_costs">
                £{{ competition.min_project_costs.toLocaleString("en-EN") }}
              </template>
              <template v-else>
                --
              </template>
            </p>
          </li>
          <li class="w-1/2 py-2">
            <h4 class="text-2xs tracking-wide uppercase text-grey-600">
              Max Project Costs
            </h4>
            <p class="text-2xl text-primary-700 leading-none">
              <template v-if="competition.max_project_costs">
                £{{ competition.max_project_costs.toLocaleString("en-EN") }}
              </template>
              <template v-else>
                --
              </template>
            </p>
          </li>
        </ul>
      </template>
    </div>
    <div class="border-t bg-grey-100 p-4 flex">
      <app-badge v-if="tagText" :icon="tagText.icon" :type="tagText.colour">
        {{ tagText.text }}
      </app-badge>
      <icon-link
        :href="route('competitions.view', { competition: competition.uuid })"
        icon="arrow-thin-right-circle"
        icon-placement="right"
        class="ml-auto"
      >
        {{ applyLinkText }}
      </icon-link>
    </div>
  </app-card>
</template>

<script>
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
export default {
  name: "UserCompetitionCard",
  props: {
    competition: {
      type: Object,
      required: true
    }
  },
  computed: {
    tagText() {
      if (!this.userHasProject) return false;

      if (this.userHasProjects.length > 1) {
        return {
          text: "Multiple",
          icon: "information",
          colour: "primary"
        };
      }

      return this.userHasProject.submitted_at
        ? {
            text: "Applied",
            icon: "check",
            colour: "positive"
          }
        : this.competition.status === "closed"
        ? {
            text: "Expired",
            icon: "close-circle",
            colour: "grey"
          }
        : {
            text: "In Progress",
            icon: "time",
            colour: "neutral"
          };
    },

    applyLinkText() {
      return this.applicationOpenRange &&
        !this.applicationActiveRange &&
        !this.userHasProject &&
        this.canApply
        ? "Review & Start your application"
        : "Review competition";
    },

    userHasProject() {
      return this.competition.user_has_project;
    },

    userHasProjects() {
      return this.competition.user_has_projects;
    },

    canApply() {
      return this.competition.company_can_apply;
    },

    userHasProjectLink() {
      if (!this.userHasProject) return;

      return !this.applicationOpenRange
        ? "View application"
        : !this.userHasProject.submitted_at
        ? "Continue application"
        : "Review application";
    },

    now() {
      return new Date();
    },

    start_date() {
      return this.parse(this.competition.start_date);
    },

    end_date() {
      return this.parse(this.competition.end_date);
    },

    application_start_date() {
      return this.parse(this.competition.application_start_date);
    },

    application_end_date() {
      return this.parse(this.competition.application_end_date);
    },

    isStarted() {
      return (
        this.competition.statuses.started ||
        this.applicationOpenRange ||
        this.applicationActiveRange ||
        this.isComplete
      );
    },

    applicationOpenRange() {
      return (
        this.competition.statuses.open ||
        this.applicationActiveRange ||
        this.isComplete
      );
    },

    applicationActiveRange() {
      return this.competition.statuses.active || this.isComplete;
    },

    isComplete() {
      return this.competition.statuses.completed;
    },

    applicationDate() {
      switch (this.competition.status) {
        case "scheduled":
          return `<strong>Submissions open ${format(
            this.application_start_date,
            "do MMMM yyyy"
          )}</strong>`;
        case "open":
          if (
            this.userHasProject &&
            this.userHasProject.application_extended_until !== null
          ) {
            return `<strong>Submissions open until ${format(
              this.application_end_date,
              "do MMMM yyyy"
            )}<br>Application extended your submission deadline until ${format(
              this.parse(this.userHasProject.application_extended_until),
              "do MMMM yyyy"
            )}</strong>`;
          }
          return `<strong>Submissions open until ${format(
            this.application_end_date,
            "do MMMM yyyy"
          )}</strong>`;
        default:
          return `<strong>Submissions closed on ${format(
            this.application_end_date,
            "do MMMM yyyy"
          )}</strong>`;
      }
      // return ['scheduled'].includes(this.competition.status)
      // 				? `<strong>Submissions open ${format(this.application_start_date, 'do MMMM yyyy')}</strong>`
      // 				: ['open'].includes(this.competition.status)
      // 					? `<strong>Submissions open until ${format(this.application_end_date, 'do MMMM yyyy')}</strong>`
      // 					: `<strong>Submissions closed on ${format(this.application_end_date, 'do MMMM yyyy')}</strong>`
    }
  },
  methods: {
    parse(date) {
      return parseISO(date);
    }
  }
};
</script>

<style></style>
